// #130 Sevkiyat Planlama — geçmiş planlar listesi (c360 list kalıbı).
// GET /api/shipmentplans (OData). Satıra tıklayınca tahtaya (/shipment-planning/:id) gider.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { C360Table, ColumnChooser, ViewIcon, EditIcon, DeleteIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { API_BASE, unwrap, pick, apiMessage, fmtDate, fmtNumber, planStatusMeta } from './shipmentPlanning/shipmentPlanningUtils';

const isDraft = (status) => (status || '').toUpperCase() === 'DRAFT';

const PAGE_SIZE_DEFAULT = 10;

function mapRow(r) {
  return {
    id: pick(r, 'id', 'Id'),
    planNo: pick(r, 'planNo', 'PlanNo', 'textId', 'TextId') ?? '',
    status: pick(r, 'status', 'Status') ?? 'DRAFT',
    distributorName: pick(r, 'distributorName', 'DistributorName') ?? '',
    planDate: pick(r, 'planDate', 'PlanDate', 'createdDate', 'CreatedDate') ?? null,
    totalKm: pick(r, 'totalKm', 'TotalKm') ?? 0,
    vehicleCount: pick(r, 'vehicleCount', 'VehicleCount') ?? 0,
    orderCount: pick(r, 'orderCount', 'OrderCount') ?? 0,
  };
}

export function ShipmentPlanningListPage() {
  usePageMeta('Sipariş Planla & Dönüştür', 'Geçmiş sevkiyat planları. Yeni plan oluşturun veya mevcut planı açın.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // #141 /rows: araç sayısı (içinde sipariş olan araç) + sipariş sayısı + distribütör adı hesaplanmış gelir.
      const res = await fetch(`${API_BASE}/rows`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrap(payload) || {};
      const rawRows = data.rows || data.Rows || (Array.isArray(data) ? data : []);
      setAllRows(rawRows.map(mapRow));
      setCurrentPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    if (!needle) return allRows;
    return allRows.filter((r) => `${r.planNo} ${r.distributorName}`.toLocaleLowerCase('tr-TR').includes(needle));
  }, [allRows, filterText]);

  const totalCount = filtered.length;
  const rows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const openNew = () => navigate('/shipment-planning/new');
  const openBoard = (row) => { if (row?.id) navigate(`/shipment-planning/${row.id}`); };
  // #141 Düzenle: yalnız taslak planlar düzenlenebilir; dönüştürülmüşte buton pasif.
  const openEdit = (row) => { if (row?.id && isDraft(row.status)) navigate(`/shipment-planning/${row.id}`); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(apiMessage(payload, 'Silme başarısız'));
      showToast('success', 'Plan silindi.');
      setDeleteConfirm(false); setSelected(null); fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız'); setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('planNo', {
      header: 'Plan No',
      cell: ({ row }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span aria-hidden="true" style={{ fontSize: 18 }}>🚚</span>
          <span style={{ fontWeight: 700 }}>{row.original.planNo || '-'}</span>
        </div>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Durum',
      cell: (info) => { const m = planStatusMeta(info.getValue()); return <StatusBadge tone={m.tone}>{m.label}</StatusBadge>; },
    }),
    columnHelper.accessor('distributorName', { header: 'Distribütör', cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('planDate', { header: 'Tarih', cell: (info) => fmtDate(info.getValue()) }),
    columnHelper.accessor('vehicleCount', {
      header: 'Araç', cell: (info) => <StatusBadge tone="neutral">{Number(info.getValue() || 0)} araç</StatusBadge>,
    }),
    columnHelper.accessor('orderCount', {
      header: 'Sipariş', cell: (info) => <StatusBadge tone="neutral">{Number(info.getValue() || 0)} sipariş</StatusBadge>,
    }),
    columnHelper.accessor('totalKm', {
      header: 'Toplam KM',
      cell: (info) => <div style={{ width: '100%', textAlign: 'right', fontWeight: 700 }}>{fmtNumber(info.getValue(), 1)} km</div>,
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => {
        const draft = isDraft(row.original.status);
        return (
          <span className="c360-actions">
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Aç" aria-label="Aç" onClick={(e) => { e.stopPropagation(); openBoard(row.original); }}><ViewIcon /></button>
            {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title={draft ? 'Düzenle' : 'Dönüştürülmüş plan düzenlenemez'} aria-label="Düzenle" disabled={!draft} style={!draft ? { opacity: 0.4, cursor: 'not-allowed' } : undefined} onClick={(e) => { e.stopPropagation(); if (draft) openEdit(row.original); }}><EditIcon /></button>}
            {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title={draft ? 'Sil' : 'Dönüştürülmüş plan silinemez'} aria-label="Sil" disabled={!draft} style={!draft ? { opacity: 0.4, cursor: 'not-allowed' } : undefined} onClick={(e) => { e.stopPropagation(); if (draft) { setSelected(row.original); setDeleteConfirm(true); } }}><DeleteIcon /></button>}
          </span>
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows, columns,
    state: { columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true, pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false, getRowId: (row) => String(row.id),
  });

  const handleExport = () => {
    if (filtered.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
    const cols = [
      { field: 'planNo', headerName: 'Plan No' },
      { headerName: 'Durum', valueGetter: ({ row }) => planStatusMeta(row.status).label },
      { field: 'distributorName', headerName: 'Distribütör' },
      { headerName: 'Tarih', valueGetter: ({ row }) => fmtDate(row.planDate) },
      { field: 'vehicleCount', headerName: 'Araç' },
      { field: 'orderCount', headerName: 'Sipariş' },
      { field: 'totalKm', headerName: 'Toplam KM' },
    ];
    exportToExcel(filtered, cols, `sevkiyat_planlari_${new Date().toISOString().slice(0, 10)}`);
    showToast('success', `${filtered.length} kayıt dışa aktarıldı`);
  };

  const toolbarNode = (
    <>
      <input style={s.filterInput} type="text" placeholder="Plan no veya distribütör ile filtrele..."
        value={filterInput} onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { setFilterText(filterInput); setCurrentPage(1); } }} />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); setFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination currentPage={currentPage} totalCount={totalCount} pageSize={pageSize} rowCount={rows.length}
      onPageChange={setCurrentPage} onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }} />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Sipariş Planla & Dönüştür"
        subtitle="Sevkiyat planları: bekleyen siparişleri araçlara dağıtın, rotalayın ve belgeye dönüştürün."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Plan</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openBoard}
          emptyText="Henüz sevkiyat planı yok. '+ Yeni Plan' ile başlayın."
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, lineHeight: 1.6 }}><strong>"{selected.planNo}"</strong> planını silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default ShipmentPlanningListPage;
